import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckCircle2, CalendarDays, Users, Copy, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const STATUS_LABELS = {
  tr: { confirmed: 'Onaylandı', pending_payment: 'Ödeme Bekliyor', cancelled: 'İptal Edildi' },
  en: { confirmed: 'Confirmed', pending_payment: 'Pending Payment', cancelled: 'Cancelled' },
};
const STATUS_COLORS = {
  confirmed: { bg: '#dcfce7', color: '#166534' },
  pending_payment: { bg: '#fef3c7', color: '#92400e' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
};

export default function ConfirmationPage() {
  const { ref } = useParams();
  const { t, language, formatPrice, BACKEND_URL, getImageUrl } = useApp();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set page title
  useEffect(() => {
    document.title = language === 'tr'
      ? 'Rezervasyon Onayı | Kastamonu Amorium Hotel | 4 Mevsim Doğa Tatili'
      : 'Booking Confirmation | Kastamonu Amorium Hotel | 4 Seasons Nature Holiday';
  }, [language]);

  useEffect(() => {
    if (!ref) { navigate('/'); return; }
    axios.get(`${BACKEND_URL}/api/reservations/ref/${ref}`)
      .then(res => {
        setReservation(res.data);
        setLoading(false);
        toast.success(language === 'tr' ? 'Rezervasyonunuz onaylandı!' : 'Your reservation is confirmed!');
      })
      .catch(() => { setLoading(false); navigate('/'); });
  }, [ref, BACKEND_URL, language, navigate]);

  const copyRef = () => {
    navigator.clipboard.writeText(ref).then(() => toast.success(language === 'tr' ? 'Kopyalandı!' : 'Copied!'));
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const nights = reservation
    ? Math.round((new Date(reservation.check_out) - new Date(reservation.check_in)) / (1000 * 60 * 60 * 24)) : 0;

  const S = {
    page: { minHeight: '100vh', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' },
    container: { maxWidth: '520px', margin: '0 auto', padding: '3rem 1.5rem 4rem' },
    card: { background: 'white', border: '1px solid #EFE7DA', borderRadius: '20px', overflow: 'hidden' },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', padding: '8px 0' },
    label: { color: '#6E7F5B', display: 'flex', alignItems: 'center', gap: '6px' },
    value: { fontWeight: 600, color: '#1F3A2E' },
    divider: { borderTop: '1px solid #EFE7DA', margin: '8px 0' },
  };

  if (loading) return (
    <div style={S.page}><Navbar />
      <div style={{ ...S.container, paddingTop: '6rem', textAlign: 'center' }}>
        <div style={{ color: '#6E7F5B', fontSize: '14px' }}>{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
      </div>
    </div>
  );

  if (!reservation) return null;
  const sc = STATUS_COLORS[reservation.status] || { bg: '#f3f4f6', color: '#374151' };
  const sl = STATUS_LABELS[language] || STATUS_LABELS['tr'];

  return (
    <div style={S.page}>
      <Navbar />
      <div style={S.container}>
        {/* Success header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ width: '64px', height: '64px', background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <CheckCircle2 size={32} style={{ color: '#166534' }} />
          </div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '30px', fontWeight: 600, color: '#1F3A2E', marginBottom: '6px' }}>
            {t('booking_confirmed')}
          </h1>
          <p style={{ fontSize: '14px', color: '#6E7F5B' }}>{t('confirmation_desc')}</p>
        </div>

        {/* Main card */}
        <div style={S.card}>
          {/* Ref + Status */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #EFE7DA' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', textAlign: 'center' }}>
              {t('booking_ref')}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <code
                data-testid="confirmation-reference-code"
                style={{ fontFamily: 'monospace', fontSize: '22px', fontWeight: 800, color: '#1F3A2E', background: '#F6F1E7', padding: '6px 16px', borderRadius: '10px' }}
              >
                {ref}
              </code>
              <button onClick={copyRef} style={{ background: 'white', border: '1px solid #D8D1C6', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', color: '#6E7F5B' }}>
                <Copy size={15} />
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <span style={{ display: 'inline-block', background: sc.bg, color: sc.color, fontSize: '12px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px' }}>
                {sl[reservation.status] || reservation.status}
              </span>
            </div>
          </div>

          {/* Room image + name */}
          {reservation.room_images?.[0] && (
            <div style={{ height: '140px', overflow: 'hidden', borderBottom: '1px solid #EFE7DA' }}>
              <img src={getImageUrl(reservation.room_images[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}

          {/* Details */}
          <div style={{ padding: '16px 24px' }}>
            {reservation.room_name && (
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 600, color: '#1F3A2E', marginBottom: '12px' }}>
                {reservation.room_name}
              </p>
            )}

            <div style={S.row}>
              <span style={S.label}><CalendarDays size={14} /> {t('check_in_date')}</span>
              <span style={S.value}>{formatDate(reservation.check_in)}</span>
            </div>
            <div style={S.row}>
              <span style={S.label}><CalendarDays size={14} /> {t('check_out_date')}</span>
              <span style={S.value}>{formatDate(reservation.check_out)}</span>
            </div>
            <div style={S.row}>
              <span style={S.label}><Users size={14} /> {t('guests')}</span>
              <span style={S.value}>{reservation.guests} {t('guest_count')}</span>
            </div>
            <div style={S.row}>
              <span style={S.label}>{t('total_nights')}</span>
              <span style={S.value}>{nights}</span>
            </div>

            <div style={S.divider} />
            <div style={S.row}>
              <span style={S.label}>
                {language === 'tr' ? 'Oda Ücreti (Vergiler Hariç)' : 'Room Rate (Excl. Taxes)'}
              </span>
              <span style={S.value}>{formatPrice(reservation.subtotal_try || reservation.total_price_try)}</span>
            </div>
            <div style={S.row}>
              <span style={S.label}>KDV ({reservation.vat_rate || 10}%)</span>
              <span style={S.value}>{formatPrice(reservation.vat_amount_try || 0)}</span>
            </div>
            <div style={S.row}>
              <span style={S.label}>{language === 'tr' ? 'Konaklama Vergisi' : 'Accommodation Tax'} ({reservation.accommodation_tax_rate ?? 1}%)</span>
              <span style={S.value}>{formatPrice(reservation.accommodation_tax_amount_try || 0)}</span>
            </div>
            <div style={S.divider} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '16px', color: '#1F3A2E', padding: '4px 0' }}>
              <span>{language === 'tr' ? 'Toplam (Vergiler Dahil)' : 'Total (Incl. Taxes)'}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(reservation.total_price_try)}</span>
            </div>
          </div>

          {/* Guest info */}
          <div style={{ padding: '14px 24px', background: '#F9F7F4', borderTop: '1px solid #EFE7DA' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>{t('guest_name')}</p>
            <p style={{ fontWeight: 700, color: '#1F3A2E', marginBottom: '2px' }}>{reservation.first_name} {reservation.last_name}</p>
            <p style={{ fontSize: '13px', color: '#6E7F5B' }}>{reservation.email}</p>
            <p style={{ fontSize: '13px', color: '#6E7F5B' }}>{reservation.phone}</p>
            {reservation.special_requests && (
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #EFE7DA' }}>
                <p style={{ fontSize: '12px', color: '#6E7F5B', marginBottom: '2px' }}>{t('special_requests')}</p>
                <p style={{ fontSize: '13px', color: '#3A3632' }}>{reservation.special_requests}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }} data-testid="confirmation-view-booking-button">
          <button
            onClick={() => navigate('/')}
            style={{ flex: 1, background: 'white', color: '#1F3A2E', padding: '13px', borderRadius: '12px', border: '1.5px solid #1F3A2E', cursor: 'pointer', fontSize: '14px', fontWeight: 700, fontFamily: 'Manrope, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1F3A2E'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#1F3A2E'; }}
          >
            <Home size={15} /> {t('home')}
          </button>
          <button
            onClick={() => navigate('/rooms')}
            style={{ flex: 1, background: '#1F3A2E', color: 'white', padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}
            onMouseEnter={e => e.currentTarget.style.background = '#3E5A3F'}
            onMouseLeave={e => e.currentTarget.style.background = '#1F3A2E'}
          >
            {language === 'tr' ? 'Yeni Rezervasyon' : 'New Booking'}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
